import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBranding, type PublicBranding, type BrandingTheme } from "@/lib/branding.functions";
import defaultLogo from "@/assets/uploads/logo_openlucht.png.asset.json";
import defaultLogoFull from "@/assets/uploads/logo_openlucht_full.png.asset.json";

type BrandingContextValue = {
  logoUrl: string;
  logoFullUrl: string;
  faviconUrl: string | null;
};

const DEFAULTS: BrandingContextValue = {
  logoUrl: defaultLogo.url,
  logoFullUrl: defaultLogoFull.url,
  faviconUrl: null,
};

const BrandingContext = createContext<BrandingContextValue>(DEFAULTS);

export function useBranding() {
  return useContext(BrandingContext);
}

const THEME_KEYS: Array<[keyof BrandingTheme, string]> = [
  ["night_deep", "--night-deep"],
  ["night", "--night"],
  ["gold", "--gold"],
  ["amber_glow", "--amber-glow"],
  ["cream", "--cream"],
  ["foreground", "--foreground"],
  ["card", "--card"],
];

function applyTheme(theme: BrandingTheme | undefined) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const [key, cssVar] of THEME_KEYS) {
    const val = theme?.[key];
    if (val && typeof val === "string" && val.trim()) {
      root.style.setProperty(cssVar, val);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
}

function applyFavicon(url: string | null) {
  if (typeof document === "undefined" || !url) return;
  const head = document.head;
  // Remove existing icon links so the new one wins.
  head.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach((n) => n.remove());
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = url;
  head.appendChild(link);
  const apple = document.createElement("link");
  apple.rel = "apple-touch-icon";
  apple.href = url;
  head.appendChild(apple);
}

export function BrandingProvider({
  children,
  initialBranding,
}: {
  children: ReactNode;
  initialBranding?: PublicBranding;
}) {
  const { data } = useQuery<PublicBranding>({
    queryKey: ["branding"],
    queryFn: () => getBranding(),
    staleTime: 5 * 60 * 1000,
    initialData: initialBranding,
  });

  useEffect(() => {
    applyTheme(data?.theme);
  }, [data?.theme]);

  useEffect(() => {
    applyFavicon(data?.faviconUrl ?? null);
  }, [data?.faviconUrl]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      logoUrl: data?.logoUrl || DEFAULTS.logoUrl,
      logoFullUrl: data?.logoFullUrl || DEFAULTS.logoFullUrl,
      faviconUrl: data?.faviconUrl ?? null,
    }),
    [data?.logoUrl, data?.logoFullUrl, data?.faviconUrl],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}